import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AuthShell, EyeToggle } from "@/components/brand";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLE_LABELS = { admin: "Administrator", city_official: "Social Equity Department", client: "Applicant" };

export default function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const checks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
  };
  const strength = Object.values(checks).filter(Boolean).length;
  const strengthLabel = ["", "Weak", "Fair", "Good", "Strong"][strength];
  const strengthColor = ["", "#c0392b", "#c47a12", "#1f5f8b", "#17a35f"][strength];

  useEffect(() => {
    if (!token) { setError("Invalid invite link."); setLoading(false); return; }
    axios.get(`${API}/auth/accept-invite/${token}`)
      .then(res => { setInvite(res.data); setLoading(false); })
      .catch(err => { setError(err.response?.data?.detail || "Invalid or expired invite."); setLoading(false); });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) { toast.error("Passwords do not match"); return; }
    if (strength < 3) { toast.error("Please choose a stronger password"); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/auth/accept-invite?token=${token}&password=${encodeURIComponent(password)}&name=${encodeURIComponent(name.trim())}`);
      localStorage.setItem("token", res.data.token);
      localStorage.setItem("email", res.data.email);
      localStorage.setItem("role", res.data.role);
      setSuccess(true);
      toast.success("Welcome to Equal!");
      setTimeout(() => {
        if (res.data.role === "admin") navigate("/admin");
        else if (res.data.role === "city_official") navigate("/city");
        else navigate("/chat");
      }, 1800);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to accept invite");
    } finally { setSubmitting(false); }
  };

  const title = error ? "Invalid Invite" : "Create your login";
  const subtitle = !error && !loading && !success && invite
    ? <>You've been invited as <strong className="text-ink">{ROLE_LABELS[invite?.role]}</strong></>
    : undefined;

  return (
    <AuthShell eyebrow="Invitation" title={title} subtitle={subtitle}>
      {loading ? (
        <div className="flex justify-center py-6"><div className="w-8 h-8 border-2 border-line border-t-equal rounded-full animate-spin" /></div>
      ) : error ? (
        <div className="space-y-4">
          <p className="text-mute text-sm">{error}</p>
          <button onClick={() => navigate("/")} className="btn-primary w-full h-11">Back to Login</button>
        </div>
      ) : success ? (
        <div className="space-y-4">
          <div className="w-12 h-12 rounded-[10px] flex items-center justify-center bg-equal">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/></svg>
          </div>
          <p className="text-ink font-semibold">You're in! Redirecting...</p>
        </div>
      ) : (
        <>
          <div className="p-3 rounded-[10px] bg-mint border border-[#cfe9db] text-sm text-equal-dark text-center">
            <strong>{invite?.email}</strong>
          </div>
          <form onSubmit={handleSubmit} className="space-y-3" autoComplete="on">
            <input type="hidden" autoComplete="username" value={invite?.email || ""} readOnly />
            <input type="text" placeholder="Full name" value={name}
              onChange={e => setName(e.target.value)}
              autoComplete="name" className="input-flat h-11"
              data-testid="invite-name-input" />
            <div className="relative">
              <input type={showPassword ? "text" : "password"} placeholder="Create a password"
                value={password} onChange={e => setPassword(e.target.value)} required
                autoComplete="new-password" id="new-password" className="input-flat h-11 pr-11" />
              <EyeToggle shown={showPassword} onClick={() => setShowPassword(!showPassword)} />
            </div>

            {password.length > 0 && (
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <div className="flex-1 flex gap-1">
                    {[1,2,3,4].map(i => (
                      <div key={i} className="h-1 flex-1 rounded-full transition-colors duration-300"
                        style={{ background: i <= strength ? strengthColor : "#e9ebed" }} />
                    ))}
                  </div>
                  <span className="text-xs font-medium" style={{ color: strengthColor }}>{strengthLabel}</span>
                </div>
              </div>
            )}

            <input type={showPassword ? "text" : "password"} placeholder="Confirm password"
              value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required
              autoComplete="new-password" className="input-flat h-11" />

            <button type="submit" disabled={submitting || strength < 3}
              className="btn-primary w-full h-11">
              {submitting ? "Setting up..." : "Create my login"}
            </button>
          </form>
        </>
      )}
    </AuthShell>
  );
}
